import React, { useEffect, useMemo, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import ChangelogFilters from './ChangelogFilters';
import ChangelogWeek from './ChangelogWeek';
import useChangelog from './useChangelog';
import { groupByWeek } from './changelogConfig';
import styles from './Changelog.module.css';

const NO_EXTRA_PROPS = () => ({});

const FeedState = ({ icon, title, text, children }) => (
  <div className={styles.state}>
    {icon && <span className={styles.stateIcon}>{icon}</span>}
    {title && <p className={styles.stateTitle}>{title}</p>}
    {text && <p className={styles.stateText}>{text}</p>}
    {children}
  </div>
);

/**
 * Lista completa das Novidades: filtros, semanas, destaques e estados de carregando, vazio e erro.
 * Usada pela tela logada e pela página pública; o que muda entre elas vem em `cardProps`.
 *
 * Props:
 *   cardProps - (entry) => { showPlan, onPlanClick, onOpenScreen }
 */
const ChangelogFeed = ({ cardProps = NO_EXTRA_PROPS }) => {
  const { entries, loading, error, reload } = useChangelog();
  const [type, setType] = useState('all');
  const [area, setArea] = useState('all');

  const areas = useMemo(
    () => Array.from(new Set(entries.map((entry) => entry.area).filter(Boolean))),
    [entries]
  );

  const weeks = useMemo(() => groupByWeek(entries.filter((entry) => (
    (type === 'all' || entry.type === type) && (area === 'all' || entry.area === area)
  ))), [entries, type, area]);

  // Âncora da semana (#2026-W41), vinda do e-mail: rola até ela depois que a lista carrega
  useEffect(() => {
    if (loading || !window.location.hash) return;
    const target = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading]);

  if (loading) {
    return (
      <FeedState icon={<CircularProgress size={28} />} text="Carregando as novidades..." />
    );
  }

  if (error) {
    return (
      <FeedState
        icon={<CloudOffOutlinedIcon />}
        title="Não foi possível carregar as novidades"
        text="Verifique sua conexão e tente de novo em instantes."
      >
        <button type="button" className={styles.retryButton} onClick={reload}>
          Tentar de novo
        </button>
      </FeedState>
    );
  }

  if (entries.length === 0) {
    return (
      <FeedState
        icon={<AutoAwesomeOutlinedIcon />}
        title="As novidades vão aparecer aqui"
        text="Toda semana a Gabriela ganha melhorias. Volte em breve para ver o que mudou."
      />
    );
  }

  const filtered = type !== 'all' || area !== 'all';

  return (
    <div className={styles.feed}>
      <ChangelogFilters type={type} onTypeChange={setType} area={area} onAreaChange={setArea} areas={areas} />

      {weeks.length === 0 ? (
        <FeedState title="Nada por aqui com esses filtros" text="Tente outro tipo ou outra área.">
          <button type="button" className={styles.retryButton} onClick={() => { setType('all'); setArea('all'); }}>
            Ver todas as novidades
          </button>
        </FeedState>
      ) : (
        weeks.map((week) => (
          <ChangelogWeek key={week.key} week={week} expandFixes={filtered && type === 'fix'} cardProps={cardProps} />
        ))
      )}
    </div>
  );
};

export default ChangelogFeed;
